import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import * as api from './markup-profiles.api';

import type {
  CreateMarkupProfileRequest,
  MarkupProfileListQuery,
  UpdateMarkupProfileRequest,
} from '@utazzo/shared';

const QK = {
  list: (query: MarkupProfileListQuery) => ['markup-profiles', 'list', query] as const,
  detail: (id: string) => ['markup-profiles', 'detail', id] as const,
};

export const useMarkupProfilesList = (query: MarkupProfileListQuery) =>
  useQuery({
    queryKey: QK.list(query),
    queryFn: () => api.listMarkupProfiles(query),
    staleTime: 30_000,
  });

export const useMarkupProfile = (id: string | null | undefined) =>
  useQuery({
    queryKey: QK.detail(id ?? ''),
    queryFn: () => api.getMarkupProfile(id as string),
    enabled: Boolean(id),
    staleTime: 30_000,
  });

export const useCreateMarkupProfile = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateMarkupProfileRequest) => api.createMarkupProfile(payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['markup-profiles', 'list'] });
      toast.success('Markup profile created');
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to create profile'),
  });
};

export const useUpdateMarkupProfile = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateMarkupProfileRequest }) =>
      api.updateMarkupProfile(id, payload),
    onSuccess: (m) => {
      qc.invalidateQueries({ queryKey: ['markup-profiles', 'list'] });
      qc.invalidateQueries({ queryKey: QK.detail(m.id) });
      toast.success('Markup profile updated');
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to update profile'),
  });
};

export const useDeleteMarkupProfile = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.deleteMarkupProfile(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['markup-profiles', 'list'] });
      toast.success('Markup profile deleted');
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to delete profile'),
  });
};

const extractMessage = (err: unknown): string | null => {
  if (err && typeof err === 'object') {
    const e = err as { message?: unknown; details?: { message?: unknown } };
    if (e.details && typeof e.details.message === 'string') return e.details.message;
    if (typeof e.message === 'string') return e.message;
  }
  if (err instanceof Error) return err.message;
  return null;
};
